import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  useHistory,
  useLocation,
  useParams,
  useRouteMatch,
} from 'react-router-dom';

import { BRAND_COLORS } from 'brand-guidelines/colors';
import {
  loginUserWithToken,
  requestClinic,
  requestUserAndPatient,
  resetMatchInsurance,
  updateBookingWidgetSlot,
  updateDirectToProfileLink,
  updateDMProps,
} from 'connect/actions';
import { UserSourceStateHandler } from 'connect/state/handlers/user';
import Profile from 'pages/profile/components/Profile/Profile';
import { googlePlaceActionLink } from 'utils/google';

import {
  selectDataClinicCountry,
  selectDataClinicLocality,
  selectDataMatchId,
} from '../../connect/selectors';
import * as Url from '../../pages/common/helpers/url-helpers';
import { FunnelProvider } from '../../pages/funnel/storage/Storage';
import { useWindowDimensions } from '../../providers/WindowDimensionsProvider';

import { AppContainer } from './styled-components';

/*
 * ProfilePage
 *
 * This is the profile route, at the '/results/:clinicId' route.
 */
const ProfilePage = () => {
  const { clinicId: clinicIdString } = useParams();
  const match = useRouteMatch();

  const dispatch = useDispatch();
  const history = useHistory();
  const { layout } = useWindowDimensions();
  const { search, pathname } = useLocation();

  const storeMatchId = useSelector(selectDataMatchId);

  const paramsArray = Url.getParam(
    search,
    'utm_source',
    'profileLink',
    'bookingWidgetSlotProvider',
    'bookingWidgetSlotDate',
    'bookingWidgetSlotTime',
    'dm_promo_code',
    'dm_referral_code',
    'dm_referral_promo',
    'auth',
    'encodedRequestData',
    'encodedRequestedAppt',
    'encodedInsurance',
    'encodedMatch',
  );

  const [
    urlUtmSource,
    urlProfileLink,
    urlBookingWidgetSlotProvider,
    urlBookingWidgetSlotDate,
    urlBookingWidgetSlotTime,
    urlPromoCode,
    urlReferralCode,
    urlReferralPromo,
    urlAuthParam,
  ] = paramsArray.map((param) => {
    // In case there are duplicate params, fall back to the first one
    if (Array.isArray(param)) {
      return param[0];
    }

    return param;
  });

  useEffect(() => {
    googlePlaceActionLink.captureConversionParams(search, clinicIdString);
  }, [clinicIdString, search]);

  // Handle magic link to return to checkout
  useEffect(() => {
    if (urlUtmSource) {
      dispatch(UserSourceStateHandler.update(urlUtmSource));
    }

    if (urlAuthParam) {
      dispatch(loginUserWithToken(urlAuthParam, true));
    }
  }, [
    dispatch,
    storeMatchId,
    urlUtmSource,
    urlAuthParam,
  ]);

  const referralCode = urlReferralCode;
  const referralPromo = urlReferralPromo;

  window.OCOptimizelyData = {
    country: useSelector(selectDataClinicCountry),
    locality: useSelector(selectDataClinicLocality),
  };

  useEffect(() => {
    if (urlProfileLink) {
      dispatch(updateDirectToProfileLink(true));

      if (urlPromoCode || referralCode || referralPromo) {
        dispatch(updateDMProps(urlPromoCode, referralCode, referralPromo));
      }

      dispatch(resetMatchInsurance());
      dispatch(requestUserAndPatient());
    }
  }, [
    dispatch,
    urlProfileLink,
    urlPromoCode,
    referralCode,
    referralPromo,
  ]);

  useEffect(() => {
    if (
      urlBookingWidgetSlotProvider
      && urlBookingWidgetSlotDate
      && urlBookingWidgetSlotTime
      && (urlProfileLink || history.action !== 'POP')
    ) {
      dispatch(
        updateBookingWidgetSlot(
          parseInt(urlBookingWidgetSlotProvider, 10),
          urlBookingWidgetSlotDate,
          parseInt(urlBookingWidgetSlotTime, 10),
        ),
      );
    }
  }, [
    history,
    urlBookingWidgetSlotProvider,
    urlBookingWidgetSlotDate,
    urlBookingWidgetSlotTime,
    dispatch,
    urlProfileLink,
  ]);

  useEffect(() => {
    if (clinicIdString.length > 0) {
      dispatch(requestClinic([parseInt(clinicIdString, 10)]));
    }
  }, [clinicIdString, dispatch]);

  useEffect(() => {
    const checkoutPath = `${match.url}/checkout`;
    if (pathname === checkoutPath || pathname === `${checkoutPath}/`) {
      history.push(`${checkoutPath}/patient`);
    }
  }, [pathname, match.url, history]);

  return (
    <FunnelProvider>
      <AppContainer background={BRAND_COLORS.white}>
        <Profile layout={layout} clinicIdString={clinicIdString} />
      </AppContainer>
    </FunnelProvider>
  );
};

ProfilePage.propTypes = {
  match: PropTypes.object.isRequired,
};

export default ProfilePage;
